// Generated by TaroBase SDK Generator: 2026-07-24T09:46:28.100Z
// Collection: gpuNodes/$nodeId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * GPU node registry for the t-hive earning network (provider side). One document per registered node. Users self-report GPU specs (no real hardware integration). ALL writes (register, pause, resume, claim accounting) go through authenticated backend endpoints signed by @constants.PROJECT_VAULT_ADDRESS — the frontend CANNOT write directly. Earning is computed per 30-second interval: while status=='active', the server accrues ratePer30s whole t-hive (base = whole * 1_000_000) per 30s elapsed since activeSince. On pause the backend banks accrued rewards into pendingBase and clears activeSince. On claim the backend transfers pendingBase (plus any live accrual) t-hive to the owner and resets accounting fields.
 */

export interface GpuNodesRequest {
  owner: AddressType;
  gpuModel: string;
  vram: number | TimeOperation | IncrementOperation | TokenAmount;
  tier: string;
  ratePerHour: number | TimeOperation | IncrementOperation | TokenAmount;
  registeredAt: number | TimeOperation | IncrementOperation | TokenAmount;
  lastClaimAt: number | TimeOperation | IncrementOperation | TokenAmount;
  totalEarned: number | TimeOperation | IncrementOperation | TokenAmount;
  isActive: boolean;
  serverType: string;
  status: string;
  activeSince: number | TimeOperation | IncrementOperation | TokenAmount;
  pendingBase: number | IncrementOperation | TokenAmount;
  ratePer30s: number | IncrementOperation | TokenAmount;
  awsRegion?: string;
  awsInstanceType?: string;
  label?: string;
  rentPricePerHourLamports: number | TimeOperation | IncrementOperation | TokenAmount;
  rentPricePerSecLamports: number | TimeOperation | IncrementOperation | TokenAmount;
  isRented: boolean;
  currentRentalId?: string;
  reputationScore: number | TimeOperation | IncrementOperation | TokenAmount;
  completedJobs: number | TimeOperation | IncrementOperation | TokenAmount;
  totalJobs: number | TimeOperation | IncrementOperation | TokenAmount;
  uptimePercent: number | TimeOperation | IncrementOperation | TokenAmount;
  lastHeartbeat?: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface GpuNodesResponse {
  owner: string;
  gpuModel: string;
  vram: number;
  tier: string;
  ratePerHour: number;
  registeredAt: number;
  lastClaimAt: number;
  totalEarned: number;
  isActive: boolean;
  serverType: string;
  status: string;
  activeSince: number;
  pendingBase: number;
  ratePer30s: number;
  awsRegion?: string;
  awsInstanceType?: string;
  label?: string;
  rentPricePerHourLamports: number;
  rentPricePerSecLamports: number;
  isRented: boolean;
  currentRentalId?: string;
  reputationScore: number;
  completedJobs: number;
  totalJobs: number;
  uptimePercent: number;
  lastHeartbeat?: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a GpuNodes operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildGpuNodes(nodeId: string, data: GpuNodesRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `gpuNodes/${nodeId}`, document: doc };
}

/**
 * SECURITY: create is allowed ONLY by @constants.PROJECT_VAULT_ADDRESS (the backend vault). Frontend registration goes through an authenticated backend endpoint. In addition to the earning and rental pricing fields, the backend initializes the node-reputation fields: reputationScore (0-10000 basis points, backend-computed), completedJobs, totalJobs, uptimePercent (0-100) — all UInt required (default 0) to avoid null arithmetic — plus optional lastHeartbeat (unix seconds). These are vault-controlled so owners cannot inflate their own reputation. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setGpuNodes(nodeId: string, data: GpuNodesRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`gpuNodes/${nodeId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting GpuNodes: ${formatError(error)}`);
    return false;
  }
}

export type GpuNodesRequestUpdate = Partial<GpuNodesRequest>;

/**
 * Build a GpuNodes update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateGpuNodes(nodeId: string, data: GpuNodesRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `gpuNodes/${nodeId}`, document: doc };
}

/**
 * SECURITY: update is allowed ONLY by @constants.PROJECT_VAULT_ADDRESS. In addition to pause/resume/claim accounting and rental-state flips, the backend recomputes and updates the reputation fields (reputationScore, completedJobs, totalJobs, uptimePercent) as workloads complete/fail and refreshes lastHeartbeat. Owners CANNOT update these directly, preventing reputation gaming. (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateGpuNodes(nodeId: string, data: GpuNodesRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`gpuNodes/${nodeId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating GpuNodes: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read the GPU node registry (all fields, including owner, tier, status, ratePer30s, pendingBase, totalEarned, the rental fields rentPricePerHourLamports/rentPricePerSecLamports/isRented/currentRentalId, and the new reputation fields reputationScore/completedJobs/totalJobs/uptimePercent/lastHeartbeat). Public so the dashboard and rental marketplace can list nodes.
   (Get Single Item)
 */
export async function getGpuNodes(nodeId: string): Promise<GpuNodesResponse | null> {
  try {
    const result = await get(`gpuNodes/${nodeId}`);
    return result as GpuNodesResponse | null;
  } catch (error) {
    console.error(`Error getting GpuNodes: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single GpuNodes document. (
  Read Operation Details: Anyone can read the GPU node registry (all fields, including owner, tier, status, ratePer30s, pendingBase, totalEarned, the rental fields rentPricePerHourLamports/rentPricePerSecLamports/isRented/currentRentalId, and the new reputation fields reputationScore/completedJobs/totalJobs/uptimePercent/lastHeartbeat). Public so the dashboard and rental marketplace can list nodes.
  )
 */
export function subscribeGpuNodes(callback: (data: GpuNodesResponse | null) => void, nodeId: string): Promise<() => Promise<void>> {
  return subscribe(`gpuNodes/${nodeId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as GpuNodesResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to GpuNodes at path '${`gpuNodes/${nodeId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many GpuNodes items from collection gpuNodes
 
  Read Operation Details: Anyone can read the GPU node registry (all fields, including owner, tier, status, ratePer30s, pendingBase, totalEarned, the rental fields rentPricePerHourLamports/rentPricePerSecLamports/isRented/currentRentalId, and the new reputation fields reputationScore/completedJobs/totalJobs/uptimePercent/lastHeartbeat). Public so the dashboard and rental marketplace can list nodes.
  
 */
export async function getManyGpuNodes(filter: string = ""): Promise<GpuNodesResponse[]> {
  try {
    const results = await get("gpuNodes", { prompt: filter });
    return Array.isArray(results) ? results as GpuNodesResponse[] : [];
  } catch (error) {
    console.error(`Error getting GpuNodes collection at path 'gpuNodes': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in GpuNodes collection at gpuNodes
 
  Read Operation Details: Anyone can read the GPU node registry (all fields, including owner, tier, status, ratePer30s, pendingBase, totalEarned, the rental fields rentPricePerHourLamports/rentPricePerSecLamports/isRented/currentRentalId, and the new reputation fields reputationScore/completedJobs/totalJobs/uptimePercent/lastHeartbeat). Public so the dashboard and rental marketplace can list nodes.
  
 */
export function subscribeManyGpuNodes(callback: (data: GpuNodesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("gpuNodes", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as GpuNodesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to GpuNodes collection at path '${"gpuNodes"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all GpuNodes items from collection gpuNodes
 
  Read Operation Details: Anyone can read the GPU node registry (all fields, including owner, tier, status, ratePer30s, pendingBase, totalEarned, the rental fields rentPricePerHourLamports/rentPricePerSecLamports/isRented/currentRentalId, and the new reputation fields reputationScore/completedJobs/totalJobs/uptimePercent/lastHeartbeat). Public so the dashboard and rental marketplace can list nodes.
  
 */
export async function getAllGpuNodes(filter: string = ""): Promise<GpuNodesResponse[]> {
  try {
    const results = await get("gpuNodes", { prompt: filter });
    return Array.isArray(results) ? results as GpuNodesResponse[] : [];
  } catch (error) {
    console.error(`Error getting GpuNodes collection at path 'gpuNodes': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in GpuNodes collection at gpuNodes
 
  Read Operation Details: Anyone can read the GPU node registry (all fields, including owner, tier, status, ratePer30s, pendingBase, totalEarned, the rental fields rentPricePerHourLamports/rentPricePerSecLamports/isRented/currentRentalId, and the new reputation fields reputationScore/completedJobs/totalJobs/uptimePercent/lastHeartbeat). Public so the dashboard and rental marketplace can list nodes.
  
 */
export function subscribeAllGpuNodes(callback: (data: GpuNodesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("gpuNodes", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as GpuNodesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to GpuNodes collection at path '${"gpuNodes"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count GpuNodes items in collection gpuNodes.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countGpuNodes(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("gpuNodes", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting GpuNodes at path 'gpuNodes': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on GpuNodes items in collection gpuNodes.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateGpuNodes(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("gpuNodes", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating GpuNodes at path 'gpuNodes': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * 
  Delete Operation Details: Allowed by the node OWNER (@data.owner == @user.address) OR the backend @constants.PROJECT_VAULT_ADDRESS. The backend delete route first settles+pays out any active rental (via rentalPayouts) before removing the node, so an in-progress rental's accrued SOL is never lost.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteGpuNodes(nodeId: string): Promise<boolean> {
  try {
    const succeeded = await set(`gpuNodes/${nodeId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting GpuNodes: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for GpuNodes for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteGpuNodes(nodeId: string): DocumentOperation {
  return { path: `gpuNodes/${nodeId}`, document: null };
}

